import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  AccreditationEligibilityCriteriaEnumType,
  AccreditationStatusEnumType,
} from '__generated__/graphql';
import { DocumentNode } from 'graphql';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

const INVESTMENT_ENTITY_QUERY = gql(`
  query getInvestmentEntityInvestorApplication($id: ID!) {
    investmentEntity(id: $id) {
      id
      investorApplication {
        id
        accreditationStatus
        accreditationEligibilityCriteria
        accreditationProofDocument {
          url
          name
        }
        eligibilityProofDocument {
          url
          name
        }
        targetAllocation
        investingExperience
      }
      investorReminders {
        type
        sentAt
        emailStatus
      }
    }
  }
`) as DocumentNode;

const UPSERT_ACCREDITATION_APPLICATION_MUTATION = gql(`
  mutation UpsertAccreditationApplication(
    $investmentEntityId: ID!
    $accreditationStatus: AccreditationStatusEnumType!
    $accreditationProofDocument: Upload
    $accreditationEligibilityCriteria: [AccreditationEligibilityCriteriaEnumType!]
  ) {
    upsertAccreditationApplication(
      investmentEntityId: $investmentEntityId
      accreditationStatus: $accreditationStatus
      accreditationProofDocument: $accreditationProofDocument
      accreditationEligibilityCriteria: $accreditationEligibilityCriteria
    ) {
      id
      accreditationStatus
      accreditationProofDocument {
        url
        name
      }
      accreditationEligibilityCriteria
    }
  }
`);

const UPSERT_INVESTOR_APPLICATION_MUTATION = gql(`
  mutation UpsertInvestorApplication(
    $investmentEntityId: ID!
    $accreditationStatus: AccreditationStatusEnumType!
    $eligibilityProofDocument: Upload
    $targetAllocation: String!
    $investingExperience: String!
  ) {
    upsertInvestorApplication(
      investmentEntityId: $investmentEntityId
      accreditationStatus: $accreditationStatus
      eligibilityProofDocument: $eligibilityProofDocument
      targetAllocation: $targetAllocation
      investingExperience: $investingExperience
    ) {
      id
      investorApplication {
        id
        eligibilityProofDocument {
          url
          name
        }
      }
    }
  }
`) as DocumentNode;

const getTargetAllocationValue = (target: string | undefined) => {
  if (!target) return null;

  switch (target.replace(/['"]/g, '')) {
    case 'I am looking to invest less than 25 lakhs':
      return 'lessThan25Lakhs';
    case 'I am looking to invest between 25-50 lakhs':
      return '25To50Lakhs';
    case 'I am looking to invest between 50 lakhs - 1 crore':
      return '50LakhsTo1Crore';
    case 'I am looking to invest more than 1 crore':
      return '1CrorePlus';
    default:
      return '';
  }
};

const getInvestingExperienceValue = (response: string | undefined) => {
  if (!response) return null;

  switch (response.replace(/['"]/g, '')) {
    case 'I have invested in startups or emerging or early-stage ventures directly/ indirectly':
      return 'invested';
    case 'I am a senior management professional with at least 10 years of experience':
      return 'seniorManagement';
    case 'I represent/ have represented a family office':
      return 'familyOffice';
    case 'I have experience as a serial entrepreneur':
      return 'entrepreneur';
    default:
      return '';
  }
};

const InvestmentEntityInvestorApplication: FC = () => {
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITY_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  const [upsertAccreditationApplication, { loading: mutationLoading, error: mutationError }] =
    useMutation(UPSERT_ACCREDITATION_APPLICATION_MUTATION, {
      refetchQueries: ['getInvestmentEntityInvestorApplication'],
    });

  const [
    upsertInvestorApplication,
    { loading: eligibilityMutationLoading, error: eligibilityMutationError },
  ] = useMutation(UPSERT_INVESTOR_APPLICATION_MUTATION, {
    refetchQueries: ['getInvestmentEntityInvestorApplication'],
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntity = data.investmentEntity;

  if (!investmentEntity.investorApplication) {
    return (
      <>
        <div className="flex flex-col gap-4 mt-4 border border-gray-200 rounded-md p-4">
          <Alert
            type="info"
            title="Accreditation Details are needed"
            message="Please enter the accreditation details for the investor application."
          />
          <FormPanel
            loading={loading || mutationLoading}
            error={error || mutationError}
            onSubmit={async formData => {
              try {
                await upsertAccreditationApplication({
                  variables: {
                    investmentEntityId,
                    accreditationStatus: formData.accreditationStatus,
                    accreditationProofDocument:
                      investmentEntity.investorApplication?.accreditationProofDocument !==
                      formData.accreditationProofDocument
                        ? formData.accreditationProofDocument
                        : undefined,
                    accreditationEligibilityCriteria:
                      formData.accreditationEligibilityCriteria || [],
                  },
                });
              } catch (err) {}
            }}
            submitButtonLabel="Submit"
          >
            <FormInput
              fullWidth
              type="radio-group"
              fieldName="accreditationStatus"
              label="Accreditation Status"
              defaultValue={undefined}
              options={[
                {
                  value: AccreditationStatusEnumType.PendingInvestorResponse,
                  label: 'Pending Investor Response',
                  color: 'bg-gray-400',
                },
                {
                  value: AccreditationStatusEnumType.InitiateProcess,
                  label: 'Initiate Process',
                  color: 'bg-amber-300',
                },
                {
                  value: AccreditationStatusEnumType.PendingAlindiaVerification,
                  label: 'Pending AL India Verification',
                  color: 'bg-orange-400',
                },
                {
                  value: AccreditationStatusEnumType.Accredited,
                  label: 'Accredited',
                  color: 'bg-green-400',
                },
                {
                  value: AccreditationStatusEnumType.NotAccredited,
                  label: 'Not Accredited',
                  color: 'bg-red-500',
                },
              ]}
            />
            <FormInput
              fieldName="accreditationProofDocument"
              label="Accreditation Proof Document *"
              helperText="Accredited Investor (AI) Certificate"
              type="upload"
              defaultValue={undefined}
            />
            <FormInput
              fullWidth
              type="checkbox-group"
              fieldName="accreditationEligibilityCriteria"
              label="Which of the following criteria do they qualify for?"
              defaultValue={undefined}
              compact={true}
              options={[
                {
                  value: AccreditationEligibilityCriteriaEnumType.AnnualIncome,
                  label: 'Annual Income',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.NetWorth,
                  label: 'Net Worth',
                },
                {
                  value: AccreditationEligibilityCriteriaEnumType.Combined,
                  label: 'Combined',
                },
              ]}
            />
          </FormPanel>
          <FormPanelWithReadMode
            loading={loading || mutationLoading}
            error={error || mutationError}
            disableEdit={true}
            onSubmit={() => {}}
            title="Eligibility Proof Document"
          >
            <FormInput
              fieldName="eligibilityProofDocument"
              label="Eligibility Proof Document *"
              helperText="Eligibility Proof Document"
              type="upload"
              defaultValue={undefined}
            />
          </FormPanelWithReadMode>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <FormPanelWithReadMode
        loading={loading || mutationLoading}
        error={error || mutationError}
        onSubmit={async formData => {
          try {
            await upsertAccreditationApplication({
              variables: {
                investmentEntityId,
                accreditationStatus: formData.accreditationStatus,
                accreditationProofDocument:
                  investmentEntity.investorApplication?.accreditationProofDocument !==
                  formData.accreditationProofDocument
                    ? formData.accreditationProofDocument
                    : undefined,
                accreditationEligibilityCriteria: formData.accreditationEligibilityCriteria || [],
              },
            });
          } catch (err) {}
        }}
        submitButtonLabel="Submit"
      >
        <FormInput
          fullWidth
          type="radio-group"
          fieldName="accreditationStatus"
          label="Accreditation Status"
          defaultValue={investmentEntity.investorApplication?.accreditationStatus}
          options={[
            {
              value: AccreditationStatusEnumType.PendingInvestorResponse,
              label: 'Pending Investor Response',
              color: 'bg-gray-400',
            },
            {
              value: AccreditationStatusEnumType.InitiateProcess,
              label: 'Initiate Process',
              color: 'bg-amber-300',
            },
            {
              value: AccreditationStatusEnumType.PendingAlindiaVerification,
              label: 'Pending AL India Verification',
              color: 'bg-orange-400',
            },
            {
              value: AccreditationStatusEnumType.Accredited,
              label: 'Accredited',
              color: 'bg-green-400',
            },
            {
              value: AccreditationStatusEnumType.NotAccredited,
              label: 'Not Accredited',
              color: 'bg-red-500',
            },
          ]}
        />
        <FormInput
          fieldName="accreditationProofDocument"
          label="Accreditation Proof Document *"
          helperText="Accredited Investor (AI) Certificate"
          type="upload"
          defaultValue={investmentEntity.investorApplication?.accreditationProofDocument}
        />
        <FormInput
          fullWidth
          type="checkbox-group"
          fieldName="accreditationEligibilityCriteria"
          label="Which of the following criteria do they qualify for?"
          defaultValue={
            investmentEntity.investorApplication?.accreditationEligibilityCriteria || []
          }
          compact={true}
          options={[
            {
              value: AccreditationEligibilityCriteriaEnumType.AnnualIncome,
              label: 'Annual Income',
            },
            {
              value: AccreditationEligibilityCriteriaEnumType.NetWorth,
              label: 'Net Worth',
            },
            {
              value: AccreditationEligibilityCriteriaEnumType.Combined,
              label: 'Combined',
            },
            {
              value: AccreditationEligibilityCriteriaEnumType.JointApplicant,
              label: 'Joint Application',
            },
          ]}
        />
      </FormPanelWithReadMode>

      <FormPanelWithReadMode
        loading={loading || eligibilityMutationLoading}
        error={error || eligibilityMutationError}
        disableEdit={false}
        onSubmit={async formData => {
          try {
            await upsertInvestorApplication({
              variables: {
                investmentEntityId,
                accreditationStatus: investmentEntity.investorApplication?.accreditationStatus,
                eligibilityProofDocument:
                  investmentEntity.investorApplication?.eligibilityProofDocument !==
                  formData.eligibilityProofDocument
                    ? formData.eligibilityProofDocument
                    : undefined,
                targetAllocation: formData.targetAllocation,
                investingExperience: formData.investingExperience,
              },
            });
          } catch (err) {}
        }}
        title="Eligibility Proof Document"
        submitButtonLabel="Submit"
      >
        <FormInput
          fieldName="eligibilityProofDocument"
          label="Eligibility Proof Document *"
          helperText="Eligibility Proof Document"
          type="upload"
          defaultValue={investmentEntity.investorApplication?.eligibilityProofDocument}
        />
        <FormInput
          fieldName="targetAllocation"
          label="Target Allocation"
          type="radio-group"
          defaultValue={
            getTargetAllocationValue(investmentEntity.investorApplication?.targetAllocation) || ''
          }
          options={[
            { label: 'Less than 25 lakhs', value: 'lessThan25Lakhs' },
            { label: '25-50 lakhs', value: '25To50Lakhs' },
            { label: '50 lakhs - 1 crore', value: '50LakhsTo1Crore' },
            { label: '1 crore +', value: '1CrorePlus' },
          ]}
        />
        <FormInput
          fieldName="investingExperience"
          label="Investing Experience"
          type="radio-group"
          defaultValue={
            getInvestingExperienceValue(
              investmentEntity.investorApplication?.investingExperience
            ) || ''
          }
          options={[
            {
              label:
                'I have invested in startups or emerging or early-stage ventures directly/ indirectly',
              value: 'invested',
            },
            {
              label: 'I am a senior management professional with at least 10 years of experience',
              value: 'seniorManagement',
            },
            {
              label: 'I represent/ have represented a family office',
              value: 'familyOffice',
            },
            { label: 'I have experience as a serial entrepreneur', value: 'entrepreneur' },
          ]}
        />
      </FormPanelWithReadMode>

      <p className="font-medium mt-8">Reminders Sent</p>
      <DataTable
        data={investmentEntity.investorReminders}
        columns={[
          {
            label: 'Type',
            fieldName: 'type',
          },
          {
            label: 'Sent At',
            fieldName: 'sentAt',
            type: 'DATETIME',
          },
          {
            label: 'Email Status',
            fieldName: 'emailStatus',
          },
        ]}
      />
    </div>
  );
};

export default InvestmentEntityInvestorApplication;
